/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import React, { useEffect, useState } from "react"
import * as Sentry from "@sentry/react"
import { Button, List, Modal, Spacer, Table } from "@cd2/components"
import { H3, P } from "@cd2/text"
import { ShowLayout } from "../Components/ShowLayout"
import { useGlobalState } from "../Components/GlobalStateProvider"
import { RouteComponentProps } from "react-router-dom"
import { ILoadingStatus } from "../types/common"
import { useConfirmation } from "../hooks/useConfirmation"
import { deliverItems } from "../utils/deliverItems"
import { IRecord } from "../types/schema"
import { useSiteAlerts } from "../hooks/useSiteAlerts"
import { ConfirmCancelButtons } from "../Components/Buttons/ConfirmCancelButtons"
import { Fields } from "@cd2/forms/build/es6"
import { KeyValueList } from "../Components/KeyValueList"
import { LinkButton } from "../Components/Buttons/LinkButton"
import { icons } from "../constants/icons"
import { buildRoute, eRoutes } from "../routes"
import { IBranchSettings, useSettings } from "../utils/useSettings"
import { useApi } from "../hooks/useApi"
import { useEvents } from "../Components/EventsList"
import { useDeliverItemsConfirmOrderAcknowledgement } from "../hooks/useDeliverItemsConfirmOrderAcknowledgement"
import { InternalOrderNoBarscanStatus } from "./GoodsReceiptsIndexPage"
import { itemTypes } from "../stories/quoteOrders/quoteOrderUtils"
import { bomItemsTableCss, bomItemTableRowClass } from "./BuildSets/utils"
import { calculateBuildQuantity } from "../utils/utils"

type IDeliveryNewPageOrdersRecord = IRecord<
  "orders",
  | "identifier"
  | "purchase_order_reference"
  | "customer"
  | "consolidated_orders_key"
  | "autoEmailOrderAcknowledgementOn"
> & {
  warehouseOverride?: IRecord<"warehouses", "disable_barscan">
  internal_transfer_to_branch: IRecord<"branches"> & { settings: IBranchSettings }
  customer: IRecord<"customers", "account_reference" | "name" | "cash_sales">
  items: (IRecord<
    "order_items",
    | "build_set_uid"
    | "stock_code"
    | "stock_location"
    | "body"
    | "category"
    | "cost_price_pennies"
    | "price_pennies"
    | "quantity"
    | "allocated_quantity"
    | "created_at"
    | "updated_at"
    | "weight"
    | "type"
    | "delivery"
    | "discountPercent"
    | "parent_uid"
  > & {
    product: IRecord<"products", "body" | "category" | "stock_code" | "cost_price_pennies"> | null
    product_location: IRecord<"product_locations", "name" | "specific_location"> | null
  })[]
}

export function DeliveryNewPage({
  match,
  history,
  invoice,
}: RouteComponentProps<{ id: string }> & { invoice?: boolean }) {
  const events = useEvents()
  const { confirmOrderAcknowledgementEmailNode, handleConfirmOrderAcknowledgementEmail } =
    useDeliverItemsConfirmOrderAcknowledgement()
  const api = useApi()
  const { currentUser } = useGlobalState()
  const alerts = useSiteAlerts()
  const [itemsForDelivery, setItemsForDelivery] = useState<{ itemId: string; quantity: number }[]>(
    [],
  )
  const [submitting, setSubmitting] = useState(false)
  const [status, setStatus] = useState<ILoadingStatus>("loading")
  const [order, setOrder] = useState<IDeliveryNewPageOrdersRecord | null>(null)
  const skeem = window.skeem
  const settings = useSettings()

  useEffect(() => {
    const fn = async () => {
      const [order] = await skeem.fetch<IDeliveryNewPageOrdersRecord>({
        orders: {
          attributes: [
            "autoEmailOrderAcknowledgementOn",
            "identifier",
            "purchase_order_reference",
            "customer",
            "consolidated_orders_key",
            { name: "warehouseOverride", attributes: ["disable_barscan"] },
            { name: "internal_transfer_to_branch", attributes: ["settings"] },
            { name: "customer", attributes: ["account_reference", "name", "cash_sales"] },
            {
              name: "items",
              attributes: [
                "build_set_uid",
                "stock_code",
                "stock_location",
                "body",
                "category",
                "cost_price_pennies",
                "price_pennies",
                "quantity",
                "allocated_quantity",
                "created_at",
                "updated_at",
                "weight",
                "type",
                "delivery",
                "discountPercent",
                "parent_uid",
                {
                  name: "product",
                  attributes: ["body", "category", "stock_code", "cost_price_pennies"],
                },
                {
                  name: "product_location",
                  attributes: ["name", "specific_location"],
                },
              ],
              sort: { by: "weight", direction: "asc" },
            },
          ],
          filter: { matches: { id: match.params.id } },
        },
      })
      setOrder(order)
      setStatus("done")
    }
    fn()
  }, [])

  const isInternalNoBarscan =
    order?.internal_transfer_to_branch &&
    (!settings.barScanOrders || order.warehouseOverride?.disable_barscan)

  const [confirmationNode, getConfirmation] = useConfirmation<{}, boolean>(({ onResolve }) => (
    <Modal
      onRequestClose={() => onResolve(false)}
      render={() => (
        <div>
          <H3>Confirm {invoice ? "Collection" : "Delivery"}</H3>
          <P>
            This will mark the {invoice ? "collection" : "delivery"} as complete and you will no
            longer be able to make any changes or edit its order. Are you sure?
          </P>
          <Spacer />
          <ConfirmCancelButtons
            onConfirm={() => onResolve(true)}
            onCancel={() => onResolve(false)}
          />
        </div>
      )}
      isOpen
    />
  ))

  if (status === "loading" || !order) {
    return null
  }

  function handleQuantityChange(
    itemId: string,
    quantity: number,
    item: IDeliveryNewPageOrdersRecord["items"][0],
  ) {
    const buildSetItems = order!.items.filter(
      cItem => cItem.type === itemTypes.buildSetItem && cItem.build_set_uid === item.build_set_uid,
    )
    const allItems = order!.items.filter(item => !item.delivery)
    const updated = allItems
      .map(allItem => {
        if (allItem.id === itemId) {
          if (!quantity) {
            return null
          }
          const subItems = buildSetItems
            .map(buildItem => {
              const buildQuantity =
                calculateBuildQuantity(buildItem.quantity, allItem.quantity) * quantity
              if (!buildQuantity) {
                return null
              }
              return { itemId: buildItem.id, quantity: buildQuantity }
            })
            .filter(Boolean)

          return [{ itemId, quantity }, ...subItems]
        }
        if (buildSetItems.some(x => x.id === allItem.id)) {
          // child items of the primary are handled above
          return null
        }
        return itemsForDelivery.find(x => x.itemId === allItem.id)
      })
      .flatMap(x => (Array.isArray(x) ? x : [x]))
      .filter(Boolean)

    if (itemValue(itemId) === quantity) {
      return
    }

    if (settings.useExplicitAllocations) {
      if (item && quantity > (item.allocated_quantity as number)) {
        alerts.genericInfo(
          <p>
            <b>Cannot add item</b> -{" "}
            {`There are only ${item.quantity} items allocated for ${
              invoice ? "collection" : "delivery"
            }.`}
          </p>,
        )
      }
    } else {
      if (item && quantity > (item.quantity as number)) {
        alerts.genericInfo(
          <p>
            <b>Cannot add item</b> -{" "}
            {`There are only ${item.quantity} items available for ${
              invoice ? "collection" : "delivery"
            }.`}
          </p>,
        )
      }
    }

    setItemsForDelivery(updated)
  }

  function deliverAllItems() {
    const updated = order?.items
      .filter(x => !x.delivery && x.type !== itemTypes.buildSetItem)
      .map(allItem => {
        const itemId = allItem.id
        const buildSetItems = order.items.filter(
          cItem =>
            cItem.type === itemTypes.buildSetItem && cItem.build_set_uid === allItem.build_set_uid,
        )
        const quantity = settings.useExplicitAllocations
          ? allItem.allocated_quantity
          : allItem.quantity
        if (allItem.id === itemId) {
          if (!quantity) {
            return null
          }
          const subItems = buildSetItems
            .map(buildItem => {
              const buildQuantity =
                calculateBuildQuantity(buildItem.quantity, allItem.quantity) * quantity
              if (!buildQuantity) {
                return null
              }
              return { itemId: buildItem.id, quantity: buildQuantity }
            })
            .filter(Boolean)

          return [{ itemId, quantity }, ...subItems]
        }
        if (buildSetItems.some(x => x.id === allItem.id)) {
          // child items of the primary are handled above
          return null
        }
        return itemsForDelivery.find(x => x.itemId === allItem.id)
      })
      .flatMap(x => (Array.isArray(x) ? x : [x]))
      .filter(Boolean)
    setItemsForDelivery(updated ?? [])
  }

  async function handleNewDelivery() {
    setSubmitting(true)

    const valid = validateDelivery()
    if (!valid) {
      setSubmitting(false)
      return
    }

    const confirmed = await getConfirmation({})
    if (!confirmed) {
      setSubmitting(false)
      return
    }

    try {
      const { delivery_id, invoice, order_identifier } = (await submitItems()) || {}
      if (order_identifier) {
        alerts.genericSuccess(
          <p>
            <b>Saved</b> - {`New order "${order_identifier}" Created`}
          </p>,
        )
      }
      if (isInternalNoBarscan) {
        await skeem.mutate({
          orders: {
            update: {
              id: order.id,
              internal_branch_transfer_status: InternalOrderNoBarscanStatus.picking,
            },
          },
        })
        events.createAction("orders", order.id, { name: "Order picked" })
      }
      if (invoice) {
        history.push(buildRoute(eRoutes["/invoices/:id/preview"], { id: delivery_id }))
      } else {
        history.push(buildRoute(eRoutes["/deliveries/:id/preview"], { id: delivery_id }))
      }

      alerts.genericSuccess(
        <p>
          <b>Saved</b> - {invoice ? "Invoice" : "Delivery"} saved successfully
        </p>,
      )
    } catch (e) {
      if (String(e).includes("has already been")) {
        const [{ delivery }] = await skeem.fetch<IRecord<"orders", "delivery">>({
          orders: {
            attributes: [{ name: "delivery" }],
            filter: { matches: { id: match.params.id } },
          },
        })
        if (invoice) {
          history.push(buildRoute(eRoutes["/invoices/:id/preview"], { id: delivery!.id }))
        } else {
          history.push(buildRoute(eRoutes["/deliveries/:id/preview"], { id: delivery!.id }))
        }
      } else {
        console.error(e)
        alerts.genericError(
          <p>
            <b>Error</b> - An error occurred while trying to save.
          </p>,
        )
      }
    } finally {
      setSubmitting(false)
    }
  }

  function validateDelivery() {
    if (!itemsForDelivery.some(item => item.quantity !== 0)) {
      alerts.genericError(
        <p>
          <b>Cannot save</b> - No items have been selected for delivery.
        </p>,
      )
      return false
    }

    if (itemsForDelivery.some(item => item.quantity < 0)) {
      alerts.genericError(
        <p>
          <b>Cannot save</b> - Values cannot be negative.
        </p>,
      )
      return false
    }

    const itemsForDeliveryWithItemDetails = itemsForDelivery.map(item => {
      const orderItem = order!.items.find(x => x.id === item.itemId)

      return {
        ...item,
        orderItemQuantity: orderItem!.quantity,
        orderItemAllocatedQuantity: orderItem!.allocated_quantity,
      }
    })

    console.log(itemsForDeliveryWithItemDetails)

    if (
      itemsForDeliveryWithItemDetails.some(
        item => (item.quantity || 0) > (item.orderItemQuantity || 0),
      )
    ) {
      alerts.genericError(
        <p>
          <b>Cannot save</b> - Values cannot exceed quantities.
        </p>,
      )
      return false
    }

    if (settings.useExplicitAllocations) {
      if (
        itemsForDeliveryWithItemDetails.some(
          item => (item.quantity || 0) > (item.orderItemAllocatedQuantity || 0),
        )
      ) {
        alerts.genericError(
          <p>
            <b>Cannot save</b> - Values cannot exceed allocated quantities.
          </p>,
        )
        return false
      }
    }

    return true
  }

  async function submitItems() {
    const items = itemsForDelivery
      .filter(item => item.quantity >= 0)
      .map(({ itemId: id, quantity }) => ({ id, quantity }))

    if (!order || !currentUser.id) {
      return
    }

    const [delivery, newOrder] = await deliverItems(
      skeem,
      currentUser,
      order.id,
      items,
      !!invoice,
      handleConfirmOrderAcknowledgementEmail,
    )

    if (delivery) {
      api.emailCustomerOrderDispatched(delivery.id).catch(e => {
        console.error("Error sending dispatched email", e)
      })

      if (order?.autoEmailOrderAcknowledgementOn === "onDelivery") {
        api.emailOrderAcknowledgement(order.id).catch(e => {
          console.error("Error sending order acknowledgement email", e)
        })
      }
    }

    events.createAction("orders", order.id, { name: "Order dispatched" })

    return {
      delivery_id: delivery.id,
      invoice: !!invoice,
      order_identifier: newOrder?.identifier,
    }
  }

  function itemValue(id: string) {
    return itemsForDelivery.find(x => x.itemId === id)?.quantity
  }

  function rowClass(value: number, max: number) {
    if (value === max) {
      return "rowValid"
    }
    if (value >= max / 2) {
      return "rowWarn"
    }
    if (value < max / 2) {
      return "rowError"
    }
    return ""
  }

  function sortStringNumberArrAlphabetically(
    arr: IDeliveryNewPageOrdersRecord["items"],
  ): IDeliveryNewPageOrdersRecord["items"] {
    return arr.sort((a, b) => {
      // Use optional chaining with nullish coalescing to handle null product_location
      const locationA = a.product_location?.specific_location ?? ""
      const locationB = b.product_location?.specific_location ?? ""

      const regex = /(\d+)|(\D+)/g // Split numbers and non-numbers
      let mA, mB

      const partsA = locationA.match(regex) || []
      const partsB = locationB.match(regex) || []

      for (let i = 0; i < Math.min(partsA.length, partsB.length); i++) {
        mA = partsA[i]
        mB = partsB[i]

        // Compare as numbers if both parts are numeric
        if (!isNaN(Number(mA)) && !isNaN(Number(mB))) {
          if (Number(mA) !== Number(mB)) {
            return Number(mA) - Number(mB)
          }
        } else if (mA !== mB) {
          // Compare as strings if not both numeric
          return mA.localeCompare(mB)
        }
      }

      // Fallback to comparing lengths or as a last resort, as strings
      return partsA.length - partsB.length || locationA.localeCompare(locationB)
    })
  }

  const baseSortedItems = sortStringNumberArrAlphabetically(order.items)
  const baseItems = baseSortedItems.filter(x => x.type !== itemTypes.buildSetItem)
  for (let i = 0; i < baseItems.length; i++) {
    const item = baseItems[i]
    if (item.type === itemTypes.buildSet) {
      const buildSetItems = baseSortedItems.filter(
        x => x.type === itemTypes.buildSetItem && x.build_set_uid === item.build_set_uid,
      )
      baseItems.splice(i + 1, 0, ...buildSetItems)
    }
  }

  return (
    <React.Fragment>
      {confirmOrderAcknowledgementEmailNode}
      {confirmationNode}
      <ShowLayout
        minWidth={600}
        breadcrumbPath={["deliveries"]}
        body={
          <KeyValueList
            labelWidth={140}
            data={[
              { label: "Order for", value: order.customer?.name },
              { label: "Customer Reference", value: order.purchase_order_reference },
              { label: "Identifier", value: order.identifier },
            ]}
            inline
          />
        }
        actions={
          <List justifyContent="flex-end" horizontal>
            <LinkButton
              to={buildRoute(eRoutes["/orders/:id/preview"], { id: order.id })}
              buttonColor="white"
              leftIcon={icons.arrowLeft}
            >
              Back
            </LinkButton>
            <Button buttonColor="white" onClick={deliverAllItems}>
              Mark All As {invoice ? "Collected" : "Dispatched"}
            </Button>
            <Button leftIcon={icons.check} onClick={handleNewDelivery} disabled={submitting}>
              Confirm
            </Button>
          </List>
        }
        title={`Mark Items As ${invoice ? "Collected" : "Dispatched"}`}
      >
        <div
          className="page"
          css={css(
            bomItemsTableCss,
            css`
              .rowValid {
                background-color: rgba(80, 200, 40, 0.2);
              }

              .rowWarn {
                background-color: rgba(230, 126, 54, 0.2);
              }

              .rowError {
                background-color: rgba(231, 76, 60, 0.2);
              }
            `,
          )}
        >
          <Table
            data={baseItems}
            rowClass={item =>
              [
                rowClass(
                  itemValue(item.id) || 0,
                  (settings.useExplicitAllocations ? item.allocated_quantity : item.quantity) || 0,
                ),
                bomItemTableRowClass(item as any),
              ].join(" ")
            }
          >
            <Table.Column heading="Stock Code">
              {row => (
                <div className={row.type === itemTypes.buildSetItem ? "left-indented" : undefined}>
                  {row.stock_code}
                </div>
              )}
            </Table.Column>
            <Table.Column heading="Description">
              {row =>
                row.type == 1 ? <b>{row.body}</b> : row.body || (row.product && row.product.body)
              }
            </Table.Column>
            {settings.useExplicitAllocations ? (
              <Table.Column heading="Allocated Quantity">
                {row => row.allocated_quantity}
              </Table.Column>
            ) : (
              <Table.Column heading="Quantity">{row => row.quantity}</Table.Column>
            )}
            <Table.Column heading="Location">
              {row => {
                if (row.product_location?.specific_location) {
                  return row.product_location.specific_location
                }
                return row.product_location?.name ?? ""
              }}
            </Table.Column>
            <Table.Column heading={`${invoice ? "Collection" : "Delivery"} Quantity`}>
              {row => (
                <Fields.Number2
                  key={row.type === itemTypes.buildSetItem ? itemValue(row.id)?.toString() : row.id}
                  inputProps={{
                    min: 0,
                    max:
                      settings.useExplicitAllocations &&
                      [itemTypes.sage, itemTypes.buildSet, itemTypes.buildSetItem].includes(
                        row.type,
                      )
                        ? row.allocated_quantity
                        : row.quantity ?? undefined,
                  }}
                  onChange={value => handleQuantityChange(row.id, Number(value), row)}
                  value={itemValue(row.id)}
                  placeholder={"0"}
                  disabled={row.type == itemTypes.buildSetItem}
                />
              )}
            </Table.Column>
          </Table>
          <Spacer />
          <div className="table-actions">
            <div className="flex-spacer" />
            <Button leftIcon={icons.check} onClick={handleNewDelivery} disabled={submitting}>
              Confirm
            </Button>
          </div>
        </div>
      </ShowLayout>
    </React.Fragment>
  )
}
